import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DriverFareCalculation,
  FareAdjustment,
  FareCalculation,
  FareReviewDetail,
  FareReviewItem,
  FareReviewStatus,
  FareReviewStatusUpdate,
  FareReviewTriggerType,
  LyftDispatchRideFareAdjustmentReason,
  LyftDispatchRideFareReviewStatusUpdate
} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class FareService extends UzurvRestService {
  readonly resourceUrl = 'fares/';
  constructor(
    protected httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    protected domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  listFareReviewItems(pageNumber: number, pageSize: number, triggerTypeName?: string): Observable<Array<FareReviewItem>> {
    return this.get<Array<FareReviewItem>>(`${this.resourceUrl}`, {
      triggerTypeName: triggerTypeName,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, FareReviewItem)));
  }

  listFareReviewTriggerTypes(): Observable<Array<FareReviewTriggerType>> {
    return this.get<Array<FareReviewTriggerType>>(`${this.resourceUrl}fareReviewTriggerTypes/`).pipe(
      map(response => Deserialize(response, FareReviewTriggerType))
    );
  }

  getFareStatusForReservation(reservationId: number): Observable<FareReviewStatus> {
    return this.get<FareReviewStatus>(`${this.resourceUrl}status/`, {
      reservationId: reservationId
    }).pipe(map(response => Deserialize(response, FareReviewStatus)));
  }

  listAllLyftDispatchRideFareAdjustmentReasons(): Observable<Array<LyftDispatchRideFareAdjustmentReason>> {
    return this.get<Array<LyftDispatchRideFareAdjustmentReason>>(`${this.resourceUrl}lyft/fareAdjustmentReasons/`).pipe(
      map(response => Deserialize(response, LyftDispatchRideFareAdjustmentReason))
    );
  }

  getFareReviewDetail(reservationId: number): Observable<FareReviewDetail> {
    return this.get<FareReviewDetail>(`${this.resourceUrl}`, {
      reservationId: reservationId
    }).pipe(map(response => Deserialize(response, FareReviewDetail)));
  }

  estimateFareAdjustment(fareAdjustment: FareAdjustment): Observable<DriverFareCalculation> {
    return this.put<FareReviewDetail>(
      `${this.resourceUrl}estimateFareAdjustment/?reservationId=${fareAdjustment.reservationId}`,
      Serialize(fareAdjustment, FareAdjustment)
    ).pipe(map(response => Deserialize(response, DriverFareCalculation)));
  }

  updateFareReviewStatus(fareReviewStatusUpdate: FareReviewStatusUpdate): Observable<DriverFareCalculation> {
    return this.put<FareReviewStatusUpdate>(
      `${this.resourceUrl}status/?reservationId=${fareReviewStatusUpdate.reservationId}`,
      Serialize(fareReviewStatusUpdate, FareReviewStatusUpdate)
    ).pipe(map(response => Deserialize(response, FareCalculation)));
  }

  updateFareReviewStatusLyft(
    lyftDispatchRideId: number,
    fareReviewStatusUpdate: LyftDispatchRideFareReviewStatusUpdate
  ): Observable<boolean> {
    return this.put<LyftDispatchRideFareReviewStatusUpdate>(
      `${this.resourceUrl}status/?lyftDispatchRideId=${lyftDispatchRideId}`,
      Serialize(fareReviewStatusUpdate, LyftDispatchRideFareReviewStatusUpdate)
    ).pipe(map(response => Deserialize(response, Boolean)));
  }

  adjustFare(fareAdjustment: FareAdjustment): Observable<FareCalculation> {
    return this.put<FareAdjustment>(
      `${this.resourceUrl}fareAdjustment/?reservationId=${fareAdjustment.reservationId}`,
      Serialize(fareAdjustment, FareAdjustment)
    ).pipe(map(response => Deserialize(response, FareCalculation)));
  }
}
